/**
 * Shared types and enums for the application
 */

/**
 * Invoice status enum.
 * Early acceleration flow: pending → early_payment_scheduled → early_payment_completed.
 */
export enum InvoiceStatus {
  PENDING = "pending",
  EARLY_PAYMENT_SCHEDULED = "early_payment_scheduled",
  EARLY_PAYMENT_COMPLETED = "early_payment_completed",
  STANDARD_PAYMENT = "standard_payment",
}

/**
 * Invoice status display labels
 */
export const InvoiceStatusLabels: Record<InvoiceStatus, string> = {
  [InvoiceStatus.PENDING]: "Pending",
  [InvoiceStatus.EARLY_PAYMENT_SCHEDULED]: "Early Payment",
  [InvoiceStatus.EARLY_PAYMENT_COMPLETED]: "Completed - Accelerated",
  [InvoiceStatus.STANDARD_PAYMENT]: "Completed - Standard",
};

/**
 * Invoice status badge colors
 */
export const InvoiceStatusColors: Record<InvoiceStatus, string> = {
  [InvoiceStatus.PENDING]: "bg-warning/10 text-warning",
  [InvoiceStatus.EARLY_PAYMENT_SCHEDULED]: "bg-success/10 text-success",
  [InvoiceStatus.EARLY_PAYMENT_COMPLETED]: "bg-primary/10 text-primary",
  [InvoiceStatus.STANDARD_PAYMENT]: "bg-muted text-muted-foreground",
};

/** Statuses that count toward budget utilization (scheduled + completed) */
export const BUDGET_UTILIZATION_STATUSES: string[] = [
  InvoiceStatus.EARLY_PAYMENT_SCHEDULED,
  InvoiceStatus.EARLY_PAYMENT_COMPLETED,
  "early_payment",
  "paid",
];

/** Statuses that represent "accelerated" (early payment path) for metrics/display */
export const ACCELERATED_STATUSES: string[] = [
  InvoiceStatus.EARLY_PAYMENT_SCHEDULED,
  InvoiceStatus.EARLY_PAYMENT_COMPLETED,
  "early_payment",
  "paid",
];

/** Invoices with this status are ready for ACH (scheduled but not yet paid) */
export const ACH_READY_STATUS = InvoiceStatus.EARLY_PAYMENT_SCHEDULED;

export function isAcceleratedStatus(status: string): boolean {
  return ACCELERATED_STATUSES.includes(status);
}

export function isBudgetUtilizationStatus(status: string): boolean {
  return BUDGET_UTILIZATION_STATUSES.includes(status);
}

/**
 * Base invoice interface
 */
export interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
  invoice_file_url: string | null;
  invoice_file_name: string | null;
}

/**
 * Parsed invoice data (before saving)
 */
export interface ParsedInvoice {
  payment_amount: number | null;
  payment_due_date: string | null;
  supplier_name: string | null;
  supplier_email: string | null;
  file_url?: string;
  file_name?: string;
}

/**
 * Settings interface
 */
export interface Settings {
  id: string;
  early_payment_budget: number;
  early_payment_days: number;
  buyer_name: string;
  notification_email: string;
  budget_period_start: string | null;
  budget_period_end: string | null;
}

/**
 * ACH record interface
 */
export interface ACHRecord {
  id: string;
  date: Date;
  invoiceCount: number;
  totalAmount: number;
  fileName: string;
  content: string;
}

/**
 * Helper function to get status label (supports legacy status strings for backward compat)
 */
export function getInvoiceStatusLabel(status: string): string {
  const legacy: Record<string, string> = {
    early_payment: InvoiceStatusLabels[InvoiceStatus.EARLY_PAYMENT_SCHEDULED],
    paid: InvoiceStatusLabels[InvoiceStatus.EARLY_PAYMENT_COMPLETED],
  };
  return legacy[status] ?? InvoiceStatusLabels[status as InvoiceStatus] ?? status;
}

/**
 * Helper function to get status color (supports legacy status strings for backward compat)
 */
export function getInvoiceStatusColor(status: string): string {
  const legacy: Record<string, string> = {
    early_payment: InvoiceStatusColors[InvoiceStatus.EARLY_PAYMENT_SCHEDULED],
    paid: InvoiceStatusColors[InvoiceStatus.EARLY_PAYMENT_COMPLETED],
  };
  return (
    legacy[status] ??
    InvoiceStatusColors[status as InvoiceStatus] ??
    "bg-muted text-muted-foreground"
  );
}
